import { useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

import { clearSignInBrowserListener } from '../../utils/common/setup-api';
import {
  hasDynatraceAuthenticationAttempt,
  trackDynatraceAuthenticationFailure,
  trackDynatraceOAuthCallbackReceived,
  trackDynatraceSignInCancelled,
} from '../../utils/native/dynatrace-events';
import { IS_IOS, IS_NATIVE } from '../constants';

export function useAuthRedirect() {
  useEffect(() => {
    if (!IS_NATIVE) return;
    // Used for OIDC auth
    CapacitorApp.addListener('appUrlOpen', async (data) => {
      const params = new URLSearchParams(data.url.split('?')?.[1]);
      const code = params.get('code');
      const state = params.get('state');
      const oauthError = params.get('error');

      if (code || oauthError) await clearSignInBrowserListener();

      if (!code) {
        if (oauthError && hasDynatraceAuthenticationAttempt()) {
          if (oauthError === 'access_denied') trackDynatraceSignInCancelled();
          else trackDynatraceAuthenticationFailure('auth_session_unavailable', 'oauth_callback', oauthError);
        }

        // Logout redirect: schneidercharge:// with no code/state params.
        // On iOS, SFSafariViewController does not auto-close when it navigates to an app
        // scheme URL — it stays open showing a blank page. Explicitly close it here so
        // the user isn't left staring at a blank overlay after logout.
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        if (IS_IOS) await Browser.close().catch(() => {});
        return;
      }

      trackDynatraceOAuthCallbackReceived();

      // The root domain of the webview differs according to the platform.
      // We reload the page with the passed query parameters to authorize with cIAM through federated identities.
      const rootDomain = IS_IOS ? 'capacitor://localhost' : 'http://localhost';
      const paramsString = `?code=${code}&state=${state ?? ''}`;

      window.location.replace(`${rootDomain}/${paramsString}`);

      // Not sure if this is a bug, but the browser does not close itself on iOS, so we have to do so manually.
      if (IS_IOS) await Browser.close();
    });
  }, []);
}
